import { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Container,
  Box,
  Stack,
  Typography,
  TextField,
  Card,
  CardContent,
  Select,
  MenuItem,
} from "@mui/material";

import ArrangementCalculator from "./ArrangementCalculator";
import { useArrangement } from "../../context/ArrangementProvider";
import { useAccount } from "../../context/AccountProvider";
import { useClient } from "../../context/ClientProvider";

const handleSubmit = (event) => {
  event.preventDefault();
};

function onChange(e) {
  return null;
}

function ArrangementForm() {
  /* The create repayment form component */

  const {
    planMode,
    plan,
    setPlan,
    paymentFrequencies = {},
    paymentMethods,
    calculateStartDate,
    calculateEndDate,
    getFinalPaymentAmount,
  } = useArrangement();
  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();
  const [errorMessage, setErrorMessage] = useState(null);
  const monthlyPick = "monthlyPick";
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Mapping between API values and display values for monthlyPickOrdinal
  const ordinalMapping = {
    'First': '1st',
    'Second': '2nd',
    'Third': '3rd',
    'Fourth': '4th',
    'Last': 'Last'
  };

  const getOrdinalNumber = (weekPosition) => {
    const ordinalToNumber = {
      'First': 1,
      'Second': 2,
      'Third': 3,
      'Fourth': 4,
      'Last': 'Last'
    };
    
    if (weekPosition === "Last") return 5;
    return ordinalToNumber[weekPosition] || Number.parseInt(weekPosition.replaceAll(/\D/g, ''));
  };

  const findLastWorkday = (date) => {
    date.setMonth(date.getMonth() + 1, 0); // Last day of month
    while (date.getDay() === 0 || date.getDay() === 6) {
      date.setDate(date.getDate() - 1);
    }
  };

  const findNthWorkday = (date, weekNumber) => {
    let workdayCount = 0;
    let attempts = 0;
    while (workdayCount < weekNumber && attempts < 50) {
      if (date.getDay() >= 1 && date.getDay() <= 5) {
        workdayCount++;
      }
      if (workdayCount < weekNumber) {
        date.setDate(date.getDate() + 1);
      }
      attempts++;
    }
  };

  const findLastSpecificDay = (date, targetDay) => {
    date.setMonth(date.getMonth() + 1, 0); // Last day of month
    let attempts = 0;
    while (date.getDay() !== targetDay && attempts < 31) {
      date.setDate(date.getDate() - 1);
      attempts++;
    }
  };

  const findNthSpecificDay = (date, weekNumber, targetDay) => {
    let dayCount = 0;
    let attempts = 0;
    while (dayCount < weekNumber && attempts < 50) {
      if (date.getDay() === targetDay) dayCount++;
      if (dayCount < weekNumber) date.setDate(date.getDate() + 1);
      attempts++;
    }
  };

  const calculateDate = (weekPosition, dayType, specificDay = null) => {
    const minDate = dayjs().add(10, 'days'); // 10-day minimum for direct debit
    let date = new Date();
    date.setDate(1); // First of current month
    const weekNumber = getOrdinalNumber(weekPosition);

    if (dayType === "Workday" || dayType === "workday") {
      if (weekPosition === "Last") {
        findLastWorkday(date);
      } else {
        findNthWorkday(date, weekNumber);
      }
    } else {
      const targetDay = daysOfWeek.indexOf(specificDay);
      if (weekPosition === "Last") {
        findLastSpecificDay(date, targetDay);
      } else {
        findNthSpecificDay(date, weekNumber, targetDay);
      }
    }
    
    // If calculated date is before minimum, move to next month
    if (dayjs(date).isBefore(minDate)) {
      date.setMonth(date.getMonth() + 1, 1);
      if (dayType === "Workday" || dayType === "workday") {
        if (weekPosition === "Last") {
          findLastWorkday(date);
        } else {
          findNthWorkday(date, weekNumber);
        }
      } else {
        const targetDay = daysOfWeek.indexOf(specificDay);
        if (weekPosition === "Last") {
          findLastSpecificDay(date, targetDay);
        } else {
          findNthSpecificDay(date, weekNumber, targetDay);
        }
      }
    }
    
    return date;
  };

  const calculateMonthlyPickEndDate = (startDate, numberOfInstalments, weekPosition, dayType) => {
    if (!startDate || !numberOfInstalments) return new Date();

    const monthsToAdd = numberOfInstalments - 1;
    const targetMonth = dayjs(startDate).add(monthsToAdd, 'month');
    const weekNumber = getOrdinalNumber(weekPosition);
    let endDate = targetMonth.startOf('month').toDate();

    if (dayType === "Workday" || dayType === "workday") {
      if (weekPosition === "Last") {
        endDate = targetMonth.endOf('month').toDate();
        findLastWorkday(endDate);
      } else {
        findNthWorkday(endDate, weekNumber);
      }
    } else {
      const targetDay = daysOfWeek.indexOf(dayType);
      if (weekPosition === "Last") {
        endDate = targetMonth.endOf('month').toDate();
        findLastSpecificDay(endDate, targetDay);
      } else {
        findNthSpecificDay(endDate, weekNumber, targetDay);
      }
    }

    return endDate;
  };

  function setPlanPaymentMethod(paymentMethod) {
    setErrorMessage(null);
    setPlan({ ...plan, paymentMethod });
  }

  function setPlanWeeklyDay(weeklyDay) {
    setErrorMessage(null);
    setPlan({ ...plan, weeklyDay });
  }

  function setPlanMonthlyDay(monthlyDay) {
    setErrorMessage(null);
    setPlan({ ...plan, monthlyDay });
  }

  const updatePlanForMonthlyPick = (paymentFrequency) => {
    const newInstalments = Math.ceil(account.currentBalance / plan.instalmentAmount);
    const newFinalPayment = getFinalPaymentAmount(account.currentBalance, plan.instalmentAmount, newInstalments);
    
    setPlan({
      ...plan,
      paymentFrequency,
      numberOfInstalments: newInstalments,
      finalPaymentAmount: newFinalPayment,
      totalPayable: account.currentBalance,
      totalRemaining: account.currentBalance,
      paymentsLeft: newInstalments,
      monthlyPickOrdinal: "Third",
      monthlyPickDay: "Wednesday"
    });
  };

  function setPlanPaymentFrequency(paymentFrequency) {
    setErrorMessage(null);

    const isMonthlyPickWithBalance = paymentFrequency === monthlyPick && account?.currentBalance && plan?.instalmentAmount;
    
    if (isMonthlyPickWithBalance) {
      updatePlanForMonthlyPick(paymentFrequency);
    } else {
      setPlan({ ...plan, paymentFrequency });
    }
  }

  function setPlanMonthlyPickOrdinal(monthlyPickOrdinal) {
    setErrorMessage(null);
    setPlan({ ...plan, monthlyPickOrdinal });
  }

  function setPlanMonthlyPickDay(monthlyPickDay) {
    setErrorMessage(null);
    // When monthlyPick day is selected, don't set weeklyDay for Workday
    const weeklyDay = monthlyPickDay === "Workday" ? null : monthlyPickDay;
    setPlan({ ...plan, monthlyPickDay, weeklyDay });
  }

  const calculateDatesForMonthlyPick = (plan) => {
    const newStartDate = calculateDate(plan.monthlyPickOrdinal, plan.monthlyPickDay, plan.monthlyPickDay);
    const newEndDate = calculateMonthlyPickEndDate(newStartDate, plan?.numberOfInstalments, plan.monthlyPickOrdinal, plan.monthlyPickDay);
    return { newStartDate, newEndDate };
  };

  const calculateDatesForOtherFrequencies = (plan) => {
    const newStartDate = calculateStartDate(
      plan?.paymentMethod,
      plan?.paymentFrequency,
      plan?.monthlyDay,
      plan?.weeklyDay
    );
    const newEndDate = calculateEndDate(
      newStartDate,
      plan?.paymentFrequency,
      plan?.numberOfInstalments,
    );
    return { newStartDate, newEndDate };
  };

  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { hasArrangement } = useArrangement();

  const hasDateChanged = (plan, newStartDate, newEndDate) => {
    const startChanged = !plan.dateCreated || new Date(newStartDate).getTime() !== new Date(plan.dateCreated).getTime();
    const endChanged = !plan.endDate || new Date(newEndDate).getTime() !== new Date(plan.endDate).getTime();
    return startChanged || endChanged;
  };

  useEffect(() => {
    if (!plan) return;

    const isMonthlyPickReady = plan?.paymentFrequency === monthlyPick && plan?.monthlyPickOrdinal && plan?.monthlyPickDay;
    
    const { newStartDate, newEndDate } = isMonthlyPickReady 
      ? calculateDatesForMonthlyPick(plan)
      : calculateDatesForOtherFrequencies(plan);

    if (hasDateChanged(plan, newStartDate, newEndDate)) {
      setPlan(prevPlan => ({
        ...prevPlan,
        dateCreated: dayjs(newStartDate),
        nextPaymentDate: dayjs(newStartDate),
        endDate: dayjs(newEndDate),
      }));
    }
  }, [
    plan?.paymentMethod,
    plan?.paymentFrequency,
    plan?.monthlyDay,
    plan?.weeklyDay,
    plan?.numberOfInstalments,
    plan?.monthlyPickOrdinal,
    plan?.monthlyPickDay,
  ]);

  // Fix wrong payment calculation on load and calculate final payment
  useEffect(() => {
    if (plan && account?.currentBalance && plan.instalmentAmount && plan.numberOfInstalments) {
      const correctFinalPayment = getFinalPaymentAmount(account.currentBalance, plan.instalmentAmount, plan.numberOfInstalments);

      // Only update if final payment is significantly different (avoid floating point precision issues)
      if (Math.abs((plan.finalPaymentAmount || 0) - correctFinalPayment) > 0.01) {
        setPlan(prevPlan => ({
          ...prevPlan,
          finalPaymentAmount: correctFinalPayment
        }));
        return; // Exit early to prevent multiple updates
      }

      // Special fix for 2 instalments with wrong payment amount
      if (plan.numberOfInstalments === 2) {
        const correctPaymentAmount = account.currentBalance / 2;
        if (Math.abs(plan.instalmentAmount - correctPaymentAmount) > 1) {
          console.log('Fixing payment amount for 2 instalments:', plan.instalmentAmount, 'to', correctPaymentAmount);
          setPlan(prevPlan => ({
            ...prevPlan,
            instalmentAmount: correctPaymentAmount,
            numberOfInstalments: 2,
            finalPaymentAmount: getFinalPaymentAmount(account.currentBalance, correctPaymentAmount, 2),
            totalPayable: account.currentBalance,
            totalRemaining: account.currentBalance,
            paymentsLeft: 2
          }));
        }
      }
    }
  }, [plan?.instalmentAmount, plan?.numberOfInstalments, plan?.finalPaymentAmount, account?.currentBalance, getFinalPaymentAmount]);

  // Debug monthly pick values
  if (plan?.paymentFrequency === monthlyPick) {
    console.log('ArrangementForm - Monthly Pick Plan:', {
      monthlyPickDay: plan?.monthlyPickDay,
      monthlyPickOrdinal: plan?.monthlyPickOrdinal,
      paymentFrequency: plan?.paymentFrequency
    });
  }

  return plan ? (
    <Card
      sx={{
        borderRadius: { xs: 0, md: 0 },
        pb: 3,
      }}
    >
      {/* Arrangement calculator section */}
      <Container
        sx={{
          marginY: 2,
        }}
      >
        <ArrangementCalculator setErrorMessage={setErrorMessage} />
      </Container>
      {/* End of arrangement calculator section */}

      <Box component='form' role='form' onSubmit={handleSubmit} noValidate>
        {/* Calculator error display */}
        <Box sx={{ mx: 3 }}>
          <Typography sx={{ color: "red" }}>
            {errorMessage && `* ${errorMessage}`}
          </Typography>
        </Box>
        <Stack
          sx={{
            paddingX: 1,
          }}
          direction={{
            xs: "column",
            md: "row",
          }}
        >
          {/* Arrangement form left column */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              component='label'
              htmlFor='instalments'
              fontWeight='bold'
            >
              {`Number of £${plan?.instalmentAmount?.toFixed(2)} instalments`}
            </Typography>
            <TextField
              margin='dense'
              required
              fullWidth
              size='small'
              id='instalments'
              name='instalments'
              value={plan?.numberOfInstalments}
              autoFocus
              InputProps={{
                readOnly: true,
                style: {
                  borderRadius: "0px",
                  backgroundColor: "#E2E2E2",
                },
              }}
            />

            <Typography
              component='label'
              htmlFor='frequency'
              fontWeight={"bold"}
              sx={{ mt: { xs: 4, md: 2 } }}
            >
              Payment frequency
            </Typography>
            <Select
              sx={{ mt: 1, borderRadius: "0px" }}
              id='frequency'
              size='small'
              value={plan?.paymentFrequency}
              onChange={(event) => setPlanPaymentFrequency(event.target.value)}
              aria-label='Payment frequency'
            >
              {Object.keys(paymentFrequencies)
                .filter(key => clientConfig?.layout.arrangements.monthlyPickEnabled || key !== 'monthlyPick')
                .map((key) => (
                  <MenuItem key={key} value={key}>
                    {paymentFrequencies[key]}
                  </MenuItem>
                ))}
            </Select>
            {clientConfig?.layout.arrangements.monthlyPickEnabled && plan?.paymentFrequency === monthlyPick && (
              <>
                <Typography
                  component='label'
                  htmlFor='frequency'
                  fontWeight={"bold"}
                  sx={{ mt: { xs: 4, md: 2 } }}
                >
                  Preferred day of the month
                </Typography>
                <Select
                  sx={{ mt: 1, borderRadius: "0px" }}
                  size='small'
                  value={plan?.monthlyPickOrdinal || ""}
                  onChange={(event) => setPlanMonthlyPickOrdinal(event.target.value)}
                >
                  <MenuItem value="First">1st</MenuItem>
                  <MenuItem value="Second">2nd</MenuItem>
                  <MenuItem value="Third">3rd</MenuItem>
                  <MenuItem value="Fourth">4th</MenuItem>
                  <MenuItem value="Last">Last</MenuItem>
                </Select>
              </>
            )}
            {planMode === "create" && (
              <>
                <Typography
                  component='label'
                  htmlFor='method'
                  fontWeight={"bold"}
                  sx={{ mt: { xs: 4, md: 2 } }}
                >
                  Payment method
                </Typography>
                <Select
                  sx={{ mt: 1, borderRadius: "0px" }}
                  id='method'
                  size='small'
                  value={plan?.paymentMethod}
                  onChange={(event) => setPlanPaymentMethod(event.target.value)}
                  aria-label='Payment method'
                >
                  <MenuItem value={paymentMethods["UK DIRECT DEBIT"]}>Direct Debit</MenuItem>
                  <MenuItem value={paymentMethods["DEBIT CARD PAYMENT"]}>Card Payment</MenuItem>
                  <MenuItem value={paymentMethods["STANDING ORDER"]}>Standing Order</MenuItem>
                  <MenuItem value={paymentMethods["CHEQUE PAYMENT"]}>Cheque</MenuItem>
                  <MenuItem value={paymentMethods["DIRECT TO BANK"]}>Bank Transfer</MenuItem>
                  <MenuItem value={paymentMethods["POSTAL ORDER"]}>Postal Order</MenuItem>
                </Select>
              </>
            )}

          </CardContent>
          {/* End of arrangement form left column */}

          {/* Arrangement form right column */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography component='label' htmlFor='amount' fontWeight={"bold"}>
              Final Payment amount
            </Typography>
            <TextField
              margin='dense'
              required
              InputProps={{
                style: {
                  borderRadius: 0,
                  backgroundColor: "#E2E2E2",
                },
              }}
              fullWidth
              size='small'
              id='amount'
              name='amount'
              value={`£${(plan?.finalPaymentAmount || 0).toFixed(2)}`}
              autoFocus
              onChange={onChange}
            />

            {plan?.paymentFrequency !== monthlyPick && (
              <>
                <Typography
                  component='label'
                  htmlFor='pay-day'
                  fontWeight={"bold"}
                  sx={{ mt: { xs: 4, md: 2 }, fontWeight: "bold" }}
                >
                  Pay on day
                </Typography>
                {plan?.paymentFrequency !== "monthly" && (
                  <Select
                    sx={{ mt: 1, borderRadius: "0px" }}
                    id='pay-weekday'
                    size='small'
                    value={plan?.weeklyDay}
                    onChange={(event) => setPlanWeeklyDay(event.target.value)}
                  >
                    <MenuItem value={"Monday"}>Monday</MenuItem>
                    <MenuItem value={"Tuesday"}>Tuesday</MenuItem>
                    <MenuItem value={"Wednesday"}>Wednesday</MenuItem>
                    <MenuItem value={"Thursday"}>Thursday</MenuItem>
                    <MenuItem value={"Friday"}>Friday</MenuItem>
                  </Select>
                )}

                {plan?.paymentFrequency === "monthly" && (
                  <Select
                    sx={{ mt: 1, borderRadius: "0px" }}
                    id='pay-day'
                    size='small'
                    value={plan?.monthlyDay}
                    onChange={(event) => setPlanMonthlyDay(event.target.value)}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(
                      (item, idx) => (
                        <MenuItem key={idx} value={item}>
                          Day {item}
                        </MenuItem>
                      )
                    )}
                  </Select>
                )}
              </>
            )}
            {clientConfig?.layout.arrangements.monthlyPickEnabled && plan?.paymentFrequency === monthlyPick && (
              <>
                <Typography
                  component='label'
                  htmlFor='pay-day'
                  fontWeight={"bold"}
                  sx={{ mt: { xs: 4, md: 2 }, fontWeight: "bold" }}
                >
                  Preferred payment date
                </Typography>
                <Select
                  sx={{ mt: 1, borderRadius: "0px" }}
                  size='small'
                  value={plan?.monthlyPickDay || ""}
                  onChange={(event) => setPlanMonthlyPickDay(event.target.value)}
                >
                  <MenuItem value="Sunday">Sunday</MenuItem>
                  <MenuItem value="Monday">Monday</MenuItem>
                  <MenuItem value="Tuesday">Tuesday</MenuItem>
                  <MenuItem value="Wednesday">Wednesday</MenuItem>
                  <MenuItem value="Thursday">Thursday</MenuItem>
                  <MenuItem value="Friday">Friday</MenuItem>
                  <MenuItem value="Saturday">Saturday</MenuItem>
                  <MenuItem value="Workday">Working day</MenuItem>
                </Select>
                {console.log('Monthly Pick Day Debug:', plan?.monthlyPickDay)}
              </>
            )}
            {/* Payment date message */}
            <Box sx={{ pt: 1 }}>
              <Typography sx={{ fontSize: 14 }}>
                {clientConfig?.layout.arrangements.monthlyPickEnabled && plan?.paymentFrequency === monthlyPick && plan?.monthlyPickOrdinal && plan?.monthlyPickDay ? (
                  <>* Your repayment schedule: <strong>{ordinalMapping[plan.monthlyPickOrdinal] || plan.monthlyPickOrdinal} {plan.monthlyPickDay} of each month</strong>
                    {plan?.numberOfInstalments && (
                      <>, estimated to end {dayjs(calculateMonthlyPickEndDate(plan?.dateCreated, plan?.numberOfInstalments, plan?.monthlyPickOrdinal, plan?.monthlyPickDay)).format("DD/MM/YYYY")}</>
                    )}</>
                ) : (
                  <>* Your new repayment plan will start on {" "}
                    <strong>{dayjs(plan?.dateCreated).format("DD/MM/YYYY")}</strong>{"."}
                    {plan?.paymentFrequency === "monthly" ?
                      " If this date falls on a weekend or bank holiday, the payment will be due the following working day." : ""}</>
                )}
              </Typography>
            </Box>
          </CardContent>
          {/* End of arrangement form right column */}
        </Stack>
      </Box>
    </Card>
  ) : null;
}

export default ArrangementForm;
